import React, { useRef, useState } from 'react'
import { faVolumeMute, faVolumeUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Col, Row } from 'antd'
import { useGeneralContext } from 'contexts/GeneralContext'
import { darkGrey, lightGrey, white } from 'lib/theme'

import placeholder from 'components/AsyncProfilePicture/placeholder.jpg'
import PlayIcon from 'components/PlayIcon/PlayIcon'
import ProfileNameAndTitle from 'components/ProfileNameAndTitle/ProfileNameAndTitle'
import VideoPlayer from 'components/VideoPlayer/VideoPlayer'
import WaveWrapper from 'components/WaveWrapper/WaveWrapper'
import Wrapper from 'components/Wrapper/Wrapper'

import styles from './ProfilePageHeader.module.css'

function ProfileVideo({ src, wave = false, children, poster }) {
  const videoPlayerRef = useRef()
  const [isPlaying, setIsPlaying] = useState(false)
  const { isMuted } = useGeneralContext()

  const mediaOverlay = (
    <section className={styles.mediaOverlayWrapper}>
      <div className={styles.mediaOverlay}>
        {children}
        <div className={styles.videoControls}>
          {isPlaying ? (
            <button
              className={styles.videoControl}
              onClick={() => videoPlayerRef.current?.toggleMute()}
              type="button"
              data-cy="toggleMuteVideoButton"
            >
              <FontAwesomeIcon
                icon={isMuted ? faVolumeMute : faVolumeUp}
                color={isMuted ? '#808080' : '000000'}
              />
            </button>
          ) : (
            <button
              className={styles.videoControl}
              onClick={() => videoPlayerRef.current?.playPause()}
              type="button"
              data-cy="playVideoButton"
            >
              <PlayIcon size={48} paused={isPlaying} />
            </button>
          )}
        </div>
      </div>
    </section>
  )

  const onVideoClick = () => {
    if (isPlaying) {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.toggleMute()
      }
    } else if (videoPlayerRef.current) {
      videoPlayerRef.current.playPause()
    }
  }

  return (
    <div
      className={styles.profileDetailsMedia}
      onClick={onVideoClick}
      role="button"
      tabIndex={0}
    >
      <div className={styles.detailsVideo}>
        <VideoPlayer
          ref={videoPlayerRef}
          src={src}
          showVideoElementInitial
          poster={poster}
          square
          autoPlay={isPlaying}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />
      </div>
      {wave ? (
        <WaveWrapper waveColor={darkGrey} bottom>
          {mediaOverlay}
        </WaveWrapper>
      ) : (
        <>{mediaOverlay}</>
      )}
    </div>
  )
}

function ProfileImage({ src, wave = false, alt, children }) {
  const mediaOverlay = (
    <section className={styles.mediaOverlayWrapper}>
      <div className={styles.mediaOverlay}>{children}</div>
    </section>
  )

  return (
    <div className={styles.profileDetailsMedia}>
      <img src={src || placeholder} alt={alt} className={styles.detailsImage} />
      {wave ? (
        <WaveWrapper waveColor={darkGrey} bottom>
          {mediaOverlay}
        </WaveWrapper>
      ) : (
        mediaOverlay
      )}
    </div>
  )
}

function ProfilePageHeader({
  profile,
  desktopHeaderContent,
  loading,
  isMobile,
}) {
  const { name } = profile || {}
  const imageUrl =
    profile?.profilePicture?.bigUrl || profile?.profilePicture?.cardUrl
  const videoUrl = profile?.profileVideo?.url
  const profileHeaderCard = (
    <ProfileNameAndTitle
      profile={profile}
      loading={loading}
      titleBelow={!isMobile}
    />
  )
  const wrappedProfileHeaderCard = <Wrapper>{profileHeaderCard}</Wrapper>

  // For desktop - use a two column layout
  if (!isMobile) {
    return (
      <WaveWrapper waveColor={profile?.description ? white : lightGrey} bottom>
        <Wrapper>
          <div className={styles.innerWrapper}>
            <Row align="top">
              <Col span={9}>
                <div className={styles.profileDetailsMedia}>
                  {videoUrl ? (
                    <ProfileVideo src={videoUrl} poster={imageUrl} />
                  ) : (
                    <ProfileImage src={imageUrl} alt={name} />
                  )}
                </div>
              </Col>
              <Col span={12} offset={3}>
                <div className={styles.desktopHeaderContent}>
                  {profileHeaderCard}
                  {desktopHeaderContent}
                </div>
              </Col>
            </Row>
          </div>
        </Wrapper>
      </WaveWrapper>
    )
  }

  return (
    <>
      {videoUrl ? (
        <ProfileVideo poster={imageUrl} src={videoUrl} wave>
          {wrappedProfileHeaderCard}
        </ProfileVideo>
      ) : (
        <ProfileImage src={imageUrl} alt={name} wave>
          {wrappedProfileHeaderCard}
        </ProfileImage>
      )}
    </>
  )
}

export default ProfilePageHeader
